import { Navigate, Route, Routes } from 'react-router-dom'
import { lazy, Suspense, type ComponentType, type ReactNode } from 'react'
import { AppLayout } from '@/components/AppLayout'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { RoleGate } from '@/components/RoleRoute'
import { LoadingState } from '@/components/ui'
import {
  canAccessAdminSettings,
  canAccessManagementAreas,
  canAccessOrganisations,
  canReviewApprovalsQueue,
  isStaffRole,
} from '@/lib/roles'

function staffOrSuperAdmin(roles: string[]) {
  return isStaffRole(roles) || roles.includes('SuperAdmin')
}

function lazyPage<T extends ComponentType<object>>(factory: () => Promise<{ default?: T } & Record<string, T>>, exportName: string) {
  return lazy(async () => {
    const mod = await factory()
    const Cmp = (mod as Record<string, T>)[exportName] ?? mod.default
    if (!Cmp) throw new Error(`Missing export ${exportName}`)
    return { default: Cmp }
  })
}

const LoginPage = lazyPage(() => import('@/pages/LoginPage'), 'LoginPage')
const SelfReferralPage = lazyPage(() => import('@/pages/SelfReferralPage'), 'SelfReferralPage')
const CsoIntakePage = lazyPage(() => import('@/pages/CsoIntakePage'), 'CsoIntakePage')
const PublicIntakePage = lazyPage(() => import('@/pages/PublicIntakePage'), 'PublicIntakePage')
const AccessDeniedPage = lazyPage(() => import('@/pages/AccessDeniedPage'), 'AccessDeniedPage')
const DashboardPage = lazyPage(() => import('@/pages/DashboardPage'), 'DashboardPage')
const ReferralsPage = lazyPage(() => import('@/pages/ReferralsPage'), 'ReferralsPage')
const ReferralDetailPage = lazyPage(() => import('@/pages/ReferralDetailPage'), 'ReferralDetailPage')
const StaffReferralCreatePage = lazyPage(() => import('@/pages/StaffReferralCreatePage'), 'StaffReferralCreatePage')
const ApprovalsPage = lazyPage(() => import('@/pages/ApprovalsPages'), 'ApprovalsPage')
const ApprovalDetailPage = lazyPage(() => import('@/pages/ApprovalsPages'), 'ApprovalDetailPage')
const ProfilePage = lazyPage(() => import('@/pages/ProfilePage'), 'ProfilePage')
const RiskAssessmentsHubPage = lazyPage(() => import('@/pages/RiskAssessmentPages'), 'RiskAssessmentsHubPage')
const RiskAssessmentCreatePage = lazyPage(() => import('@/pages/RiskAssessmentPages'), 'RiskAssessmentCreatePage')
const RiskAssessmentEditPage = lazyPage(() => import('@/pages/RiskAssessmentPages'), 'RiskAssessmentEditPage')
const RiskAssessmentDetailPage = lazyPage(() => import('@/pages/RiskAssessmentPages'), 'RiskAssessmentDetailPage')
const SessionsWorkspacePage = lazyPage(() => import('@/pages/SessionsWorkspacePage'), 'SessionsWorkspacePage')
const CapacityPage = lazyPage(() => import('@/pages/CapacityPage'), 'CapacityPage')
const RosterPage = lazyPage(() => import('@/features/ops/pages/RosterPage'), 'RosterPage')
const TimesheetsPage = lazyPage(() => import('@/features/ops/pages/TimesheetsPage'), 'TimesheetsPage')
const SessionDetailPage = lazyPage(() => import('@/pages/SessionDetailPage'), 'SessionDetailPage')
const IncidentsPage = lazyPage(() => import('@/features/ops/pages/IncidentsPages'), 'IncidentsPage')
const IncidentDetailPage = lazyPage(() => import('@/features/ops/pages/IncidentsPages'), 'IncidentDetailPage')
const ComplaintsPage = lazyPage(() => import('@/features/ops/pages/ComplaintsPages'), 'ComplaintsPage')
const ComplaintDetailPage = lazyPage(() => import('@/features/ops/pages/ComplaintsPages'), 'ComplaintDetailPage')
const RegistersPage = lazyPage(() => import('@/features/ops/pages/RegistersPage'), 'RegistersPage')
const StaffPage = lazyPage(() => import('@/pages/StaffPages'), 'StaffPage')
const StaffDetailPage = lazyPage(() => import('@/pages/StaffPages'), 'StaffDetailPage')
const RoomsPage = lazyPage(() => import('@/pages/RoomsPage'), 'RoomsPage')
const AssetsPage = lazyPage(() => import('@/pages/AssetsPage'), 'AssetsPage')
const BillingPage = lazyPage(() => import('@/features/ops/pages/BillingPage'), 'BillingPage')
const CheckoutPage = lazyPage(() => import('@/features/ops/pages/BillingPage'), 'CheckoutPage')
const FinanceSettingsPage = lazyPage(() => import('@/pages/SettingsPages'), 'FinanceSettingsPage')
const BookingTypesSettingsPage = lazyPage(() => import('@/pages/SettingsPages'), 'BookingTypesSettingsPage')
const BookingTypeEditorPage = lazyPage(() => import('@/pages/SettingsPages'), 'BookingTypeEditorPage')
const RiskAssessmentSettingsPage = lazyPage(() => import('@/pages/SettingsPages'), 'RiskAssessmentSettingsPage')
const SystemSettingsPage = lazyPage(() => import('@/pages/SettingsPages'), 'SystemSettingsPage')
const OrganisationsPage = lazyPage(() => import('@/pages/OrganisationsPage'), 'OrganisationsPage')
const OrganisationCreatePage = lazyPage(
  () => import('@/pages/OrganisationsPage'),
  'OrganisationCreatePage',
)
const OrganisationEditPage = lazyPage(
  () => import('@/pages/OrganisationsPage'),
  'OrganisationEditPage',
)

function PageSuspense({ children }: { children: ReactNode }) {
  return <Suspense fallback={<LoadingState label="Loading…" />}>{children}</Suspense>
}

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PageSuspense>
            <LoginPage />
          </PageSuspense>
        }
      />
      <Route
        path="/self-referral"
        element={
          <PageSuspense>
            <SelfReferralPage />
          </PageSuspense>
        }
      />
      <Route
        path="/cso-intake"
        element={
          <PageSuspense>
            <CsoIntakePage />
          </PageSuspense>
        }
      />
      <Route
        path="/intake/:token"
        element={
          <PageSuspense>
            <PublicIntakePage />
          </PageSuspense>
        }
      />

      <Route element={<ProtectedRoute />}>
        <Route
          path="access-denied"
          element={
            <PageSuspense>
              <AccessDeniedPage />
            </PageSuspense>
          }
        />

        <Route element={<RoleGate predicate={staffOrSuperAdmin} />}>
          <Route element={<AppLayout />}>
            <Route
              index
              element={
                <PageSuspense>
                  <DashboardPage />
                </PageSuspense>
              }
            />
            <Route
              path="referrals"
              element={
                <PageSuspense>
                  <ReferralsPage />
                </PageSuspense>
              }
            />
            <Route
              path="referrals/new"
              element={
                <PageSuspense>
                  <StaffReferralCreatePage />
                </PageSuspense>
              }
            />
            <Route
              path="referrals/:id"
              element={
                <PageSuspense>
                  <ReferralDetailPage />
                </PageSuspense>
              }
            />

            <Route element={<RoleGate predicate={canReviewApprovalsQueue} />}>
              <Route
                path="approvals"
                element={
                  <PageSuspense>
                    <ApprovalsPage />
                  </PageSuspense>
                }
              />
              <Route
                path="approvals/:id"
                element={
                  <PageSuspense>
                    <ApprovalDetailPage />
                  </PageSuspense>
                }
              />
            </Route>

            <Route
              path="profile"
              element={
                <PageSuspense>
                  <ProfilePage />
                </PageSuspense>
              }
            />
            <Route
              path="risk-assessments"
              element={
                <PageSuspense>
                  <RiskAssessmentsHubPage />
                </PageSuspense>
              }
            />
            <Route
              path="risk-assessments/new/:referralId"
              element={
                <PageSuspense>
                  <RiskAssessmentCreatePage />
                </PageSuspense>
              }
            />
            <Route
              path="risk-assessments/:referralId/:raId/edit"
              element={
                <PageSuspense>
                  <RiskAssessmentEditPage />
                </PageSuspense>
              }
            />
            <Route
              path="risk-assessments/:referralId/:raId"
              element={
                <PageSuspense>
                  <RiskAssessmentDetailPage />
                </PageSuspense>
              }
            />

            <Route path="schedule" element={<Navigate to="/calendar?mode=list" replace />} />
            <Route
              path="calendar"
              element={
                <PageSuspense>
                  <SessionsWorkspacePage />
                </PageSuspense>
              }
            />
            <Route
              path="capacity"
              element={
                <PageSuspense>
                  <CapacityPage />
                </PageSuspense>
              }
            />
            <Route
              path="roster"
              element={
                <PageSuspense>
                  <RosterPage />
                </PageSuspense>
              }
            />
            <Route
              path="timesheets"
              element={
                <PageSuspense>
                  <TimesheetsPage />
                </PageSuspense>
              }
            />
            <Route
              path="sessions/:id"
              element={
                <PageSuspense>
                  <SessionDetailPage />
                </PageSuspense>
              }
            />
            <Route
              path="incidents"
              element={
                <PageSuspense>
                  <IncidentsPage />
                </PageSuspense>
              }
            />
            <Route
              path="incidents/:id"
              element={
                <PageSuspense>
                  <IncidentDetailPage />
                </PageSuspense>
              }
            />

            <Route element={<RoleGate predicate={canAccessManagementAreas} />}>
              <Route
                path="complaints"
                element={
                  <PageSuspense>
                    <ComplaintsPage />
                  </PageSuspense>
                }
              />
              <Route
                path="complaints/:id"
                element={
                  <PageSuspense>
                    <ComplaintDetailPage />
                  </PageSuspense>
                }
              />
              <Route
                path="registers"
                element={
                  <PageSuspense>
                    <RegistersPage />
                  </PageSuspense>
                }
              />
              <Route
                path="staff"
                element={
                  <PageSuspense>
                    <StaffPage />
                  </PageSuspense>
                }
              />
              <Route
                path="staff/:id"
                element={
                  <PageSuspense>
                    <StaffDetailPage />
                  </PageSuspense>
                }
              />
              <Route
                path="rooms"
                element={
                  <PageSuspense>
                    <RoomsPage />
                  </PageSuspense>
                }
              />
              <Route
                path="assets"
                element={
                  <PageSuspense>
                    <AssetsPage />
                  </PageSuspense>
                }
              />
              <Route
                path="billing"
                element={
                  <PageSuspense>
                    <BillingPage />
                  </PageSuspense>
                }
              />
              <Route
                path="checkout/:checkoutId"
                element={
                  <PageSuspense>
                    <CheckoutPage />
                  </PageSuspense>
                }
              />
            </Route>

            <Route element={<RoleGate predicate={canAccessAdminSettings} />}>
              <Route
                path="settings/finance"
                element={
                  <PageSuspense>
                    <FinanceSettingsPage />
                  </PageSuspense>
                }
              />
              <Route
                path="settings/booking-types"
                element={
                  <PageSuspense>
                    <BookingTypesSettingsPage />
                  </PageSuspense>
                }
              />
              <Route
                path="settings/booking-types/new"
                element={
                  <PageSuspense>
                    <BookingTypeEditorPage />
                  </PageSuspense>
                }
              />
              <Route
                path="settings/booking-types/:id"
                element={
                  <PageSuspense>
                    <BookingTypeEditorPage />
                  </PageSuspense>
                }
              />
              <Route
                path="settings/risk"
                element={
                  <PageSuspense>
                    <RiskAssessmentSettingsPage />
                  </PageSuspense>
                }
              />
              <Route
                path="settings/system"
                element={
                  <PageSuspense>
                    <SystemSettingsPage />
                  </PageSuspense>
                }
              />
            </Route>

            <Route element={<RoleGate predicate={canAccessOrganisations} />}>
              <Route
                path="settings/organizations"
                element={
                  <PageSuspense>
                    <OrganisationsPage />
                  </PageSuspense>
                }
              />
              <Route
                path="settings/organizations/new"
                element={
                  <PageSuspense>
                    <OrganisationCreatePage />
                  </PageSuspense>
                }
              />
              <Route
                path="settings/organizations/:id"
                element={
                  <PageSuspense>
                    <OrganisationEditPage />
                  </PageSuspense>
                }
              />
            </Route>
            <Route path="organisations" element={<Navigate to="/settings/organizations" replace />} />
            <Route path="organizations" element={<Navigate to="/settings/organizations" replace />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
